import * as constants from './FMTreeConstants';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import axios, { AxiosResponse, AxiosError } from 'axios';
import { showGeofenceOnMap } from '../../features/FMMap/FMMapActions';
import {
    FMTreeGroupData,
    FMTreeItemDataType,
    FMTreeDataItem,
    TreeDisplayStateType,
    SelectedTreeNode,
    TreeNodeType,
    TreeType,
    FMTreeSearchData,
    FMTreeItemData,
    NodeInfoData,
    TreeViewType,
    GeofenceType,
    NodeEditData,
    GeofenceEditData,
    ParamSysName,
    FMTreeGeofenceItemData,
    NodeParam,
    NodeTextParam,
    NodeNumberParam,
    ParamType,
    OptimizeData,
    MIN_DISTANCE_BETWEEN_POINTS_DEFAULT_VALUE,
    MIN_DISTANCE_BETWEEN_POINTS_MIN_VALUE,
    ALLOWED_DEVIATION_FROM_LINE_DEFAULT_VALUE,
    ALLOWED_DEVIATION_FROM_LINE_MIN_VALUE,
    GeoPoint,
} from './FMTreeReducer';
import { RootState } from '../index';
import {
    getNodeData,
    getCheckedNodesIds,
    getExpandedGroupsIds,
    getAllGroupsIds,
    getAllItemsIds,
    getSearchData,
    getParentId,
    getIsEditModeEnabled,
    getSelectedNode,
    getChildGroups,
    getChildItems,
    isNodeChecked,
    getItemsByIds,
    getNodeEditData,
    getGroupsByIds,
    isTreeFetching,
    getNodeInfoData,
} from './FMTReeSelectors';

export interface ReplaceFetching {
    type: constants.REPLACE_FETCHING;
    treeType: TreeType;
    isFetching: boolean;
}

export interface ReplaceItemsAndGroups {
    type: constants.REPLACE_ITEMS_AND_GROUPS_DATA;
    treeType: TreeType;
    data: Record<TreeNodeType, FMTreeDataItem>;
}

export interface ReplaceGroupsData {
    type: constants.REPLACE_GROUPS_DATA;
    treeType: TreeType;
    byIds: Record<string, FMTreeGroupData | FMTreeItemDataType>;
    allIds: string[];
}

export interface ReplaceItemsData {
    type: constants.REPLACE_ITEMS_DATA;
    treeType: TreeType;
    byIds: Record<string, FMTreeGroupData | FMTreeItemDataType>;
    allIds: string[];
}

export interface ReplaceExpandedNodes {
    type: constants.REPLACE_EXPANDED_NODES;
    expanded: string[];
    treeDisplayState: TreeDisplayStateType;
    treeNodeType: TreeNodeType;
}

export interface ReplaceCheckedNodes {
    type: constants.REPLACE_CHECKED_NODES;
    checked: string[];
    treeDisplayState: TreeDisplayStateType;
    treeNodeType: TreeNodeType;
}

export interface ReplaceSelectedNode {
    type: constants.REPLACE_SELECTED_NODE;
    treeDisplayState: TreeDisplayStateType;
    selectedNode: SelectedTreeNode;
}

export interface ReplaceSearchData {
    type: constants.REPLACE_SEARCH_DATA;
    treeDisplayState: TreeDisplayStateType;
    searchData: FMTreeSearchData;
}

export interface ReplaceNodeData {
    type: constants.REPLACE_NODE_DATA;
    treeType: TreeType;
    nodeData: FMTreeGroupData | FMTreeItemDataType;
    nodeType: TreeNodeType;
}

export interface ReplaceNodeInfoData {
    type: constants.REPLACE_NODE_INFO_DATA;
    treeDisplayState: TreeDisplayStateType;
    nodeData: NodeInfoData;
}

export interface ReplaceNodeEditData {
    type: constants.REPLACE_NODE_EDIT_DATA;
    treeDisplayState: TreeDisplayStateType;
    nodeData: NodeEditData;
}

export interface ReplaceTreeViewType {
    type: constants.REPLACE_TREE_VIEW_TYPE;
    treeDisplayState: TreeDisplayStateType;
    treeViewType: TreeViewType;
}

export interface ReplaceChildGroupsIds {
    type: constants.REPLACE_CHILD_GROUPS_IDS;
    treeType: TreeType;
    groupId: string;
    childGroupsIds: string[];
}

export interface ReplaceChildItemsIds {
    type: constants.REPLACE_CHILD_ITEMS_IDS;
    treeType: TreeType;
    groupId: string;
    childItemsIds: string[];
}

export interface ReplaceAllIds {
    type: constants.REPLACE_ALL_IDS;
    treeType: TreeType;
    treeNodeType: TreeNodeType;
    allIds: string[];
}


export interface ReplaceInfoPanelFetching {
    type: constants.REPLACE_INFO_PANEL_FETCHING;
    treeDisplayState: string;
    isFetching: boolean;
}

interface BaseAction {
    treeDisplayState: string;
}

export interface ReplaceInfoPanelEditMode extends BaseAction {
    type: constants.REPLACE_INFO_PANEL_EDIT_MODE;
    infoPanelInEditMode: boolean;
}

export interface ReplaceInfoPanelOptimizeMode extends BaseAction {
    type: constants.REPLACE_INFO_PANEL_OPTIMIZE_MODE;
    infoPanelInOptimizeMode: boolean;
}

export interface ReplaceOptimizeData {
    type: constants.REPLACE_OPTIMIZE_DATA;
    payload: OptimizeData;
}

export interface ReplaceOptimizeMinDistanceBetweenPoints {
    type: constants.REPLACE_OPTIMIZE_MIN_DISTANCE_BETWEEN_POINTS;
    payload: number;
}

export interface ReplaceOptimizeAllowedDeviationFromLine {
    type: constants.REPLACE_OPTIMIZE_ALLOWED_DEVIATION_FROM_LINE;
    payload: number;
}

export interface CloseOptimizeEditMode {
    type: constants.CLOSE_OPTIMIZE_EDIT_MODE;
}

export interface ReplaceEditHasChanged {
    type: constants.REPLACE_EDIT_HAS_CHANGED;
    payload: boolean;
}

export interface ReplaceNewArea {
    type: constants.REPLACE_NEW_AREA;
    payload: number;
}

export interface ReplaceOptimizeFetching {
    type: constants.REPLACE_OPTIMIZE_FETCHING;
    payload: boolean;
}

export type Action = ReplaceItemsAndGroups
    | ReplaceGroupsData
    | ReplaceItemsData
    | ReplaceSelectedNode
    | ReplaceInfoPanelEditMode
    | ReplaceInfoPanelOptimizeMode
    | ReplaceExpandedNodes
    | ReplaceCheckedNodes
    | ReplaceNodeData
    | ReplaceNodeInfoData
    | ReplaceNodeEditData
    | ReplaceTreeViewType
    | ReplaceChildGroupsIds
    | ReplaceChildItemsIds
    | ReplaceAllIds
    | ReplaceSearchData
    | ReplaceFetching
    | ReplaceOptimizeData
    | ReplaceOptimizeMinDistanceBetweenPoints
    | ReplaceOptimizeAllowedDeviationFromLine
    | CloseOptimizeEditMode
    | ReplaceEditHasChanged
    | ReplaceNewArea
    | ReplaceOptimizeFetching
    | ReplaceInfoPanelFetching;

export const replaceFetching = (
    treeType: TreeType,
    isFetching: boolean
): ReplaceFetching => ({
    type: constants.REPLACE_FETCHING,
    isFetching,
    treeType
});

export const replaceItemsAndGroups = (
    data: Record<TreeNodeType, FMTreeDataItem>,
    treeType: TreeType
): ReplaceItemsAndGroups => ({
    type: constants.REPLACE_ITEMS_AND_GROUPS_DATA,
    data,
    treeType
});

export const replaceGroupsData = (
    treeType: TreeType,
    byIds: Record<string, FMTreeGroupData | FMTreeItemDataType>,
    allIds: string[],
): ReplaceGroupsData => ({
    type: constants.REPLACE_GROUPS_DATA,
    treeType,
    byIds,
    allIds,
});

export const replaceItemsData = (
    treeType: TreeType,
    byIds: Record<string, FMTreeGroupData | FMTreeItemDataType>,
    allIds: string[],
): ReplaceItemsData => ({
    type: constants.REPLACE_ITEMS_DATA,
    treeType,
    byIds,
    allIds,
});

export const replaceExpandedNodes = (
    treeDisplayState: TreeDisplayStateType,
    expanded: string[],
    treeNodeType: TreeNodeType
): ReplaceExpandedNodes => ({
    type: constants.REPLACE_EXPANDED_NODES,
    treeDisplayState,
    expanded,
    treeNodeType
});

export const replaceCheckedNodes = (
    treeDisplayState: TreeDisplayStateType,
    checked: string[],
    treeNodeType: TreeNodeType
): ReplaceCheckedNodes => ({
    type: constants.REPLACE_CHECKED_NODES,
    treeDisplayState,
    checked,
    treeNodeType
});

export const replaceSelectedNode = (
    treeDisplayState: TreeDisplayStateType,
    selectedNode: SelectedTreeNode
): ReplaceSelectedNode => ({
    type: constants.REPLACE_SELECTED_NODE,
    treeDisplayState,
    selectedNode
});

export const replaceSearchData = (
    treeDisplayState: TreeDisplayStateType,
    searchData: FMTreeSearchData,
): ReplaceSearchData => ({
    type: constants.REPLACE_SEARCH_DATA,
    treeDisplayState,
    searchData,
});

export const replaceInfoPanelFetching = (
    treeDisplayState: string,
    isFetching: boolean,
): ReplaceInfoPanelFetching => ({
    type: constants.REPLACE_INFO_PANEL_FETCHING,
    treeDisplayState,
    isFetching
});

export const replaceInfoPanelEditMode = (
    treeDisplayState: string,
    infoPanelInEditMode: boolean,
): ReplaceInfoPanelEditMode => ({
    type: constants.REPLACE_INFO_PANEL_EDIT_MODE,
    treeDisplayState,
    infoPanelInEditMode,
});

export const replaceNodeData = (
    treeType: TreeType,
    nodeData: FMTreeGroupData | FMTreeItemDataType,
    nodeType: TreeNodeType,
): ReplaceNodeData => ({
    type: constants.REPLACE_NODE_DATA,
    treeType,
    nodeData,
    nodeType,
});

export const replaceNodeInfoData = (
    treeDisplayState: TreeDisplayStateType,
    nodeData: NodeInfoData,
): ReplaceNodeInfoData => ({
    type: constants.REPLACE_NODE_INFO_DATA,
    treeDisplayState,
    nodeData,
});

export const replaceNodeEditData = (
    treeDisplayState: TreeDisplayStateType,
    nodeData: NodeEditData,
): ReplaceNodeEditData => ({
    type: constants.REPLACE_NODE_EDIT_DATA,
    treeDisplayState,
    nodeData,
});

export const replaceTreeViewType = (
    treeDisplayState: TreeDisplayStateType,
    treeViewType: TreeViewType,
): ReplaceTreeViewType => ({
    type: constants.REPLACE_TREE_VIEW_TYPE,
    treeDisplayState,
    treeViewType,
});

export const replaceChildGroupsIds = (
    treeType: TreeType,
    groupId: string,
    childGroupsIds: string[],
): ReplaceChildGroupsIds => ({
    type: constants.REPLACE_CHILD_GROUPS_IDS,
    treeType,
    groupId,
    childGroupsIds,
});

export const replaceChildItemsIds = (
    treeType: TreeType,
    groupId: string,
    childItemsIds: string[],
): ReplaceChildItemsIds => ({
    type: constants.REPLACE_CHILD_ITEMS_IDS,
    treeType,
    groupId,
    childItemsIds,
});

export const replaceAllIds = (
    treeType: TreeType,
    treeNodeType: TreeNodeType,
    allIds: string[],
): ReplaceAllIds => ({
    type: constants.REPLACE_ALL_IDS,
    treeType,
    treeNodeType,
    allIds,
});

export const selectNode = (
    treeDisplayState: TreeDisplayStateType,
    type: TreeNodeType,
    id: string
): ThunkAction<{}, RootState, {}, AnyAction> =>
    async (dispatch, getState) => {
        const state = getState();

        const selectedNode = getSelectedNode(state, treeDisplayState);
        const isMainGeofenceTree = treeDisplayState === TreeDisplayStateType.mainGeofencesTree;
        if (!selectedNode || id !== selectedNode.id || type !== selectedNode.type) {
            if (isMainGeofenceTree && getIsEditModeEnabled(state, treeDisplayState)) {
                dispatch(replaceInfoPanelEditMode(treeDisplayState, false));
            }

            dispatch(replaceSelectedNode(treeDisplayState, { type, id }));
        }

        if (isMainGeofenceTree) {
            dispatch(showGeofenceOnMap());

            let data = selectedNode && id === selectedNode.id && type === selectedNode.type
                ? getNodeInfoData(state, treeDisplayState)
                : await dispatch(fetchGeotreeNodeData());

            return data;
        }

        return undefined;
    };

let cancelGeoTreeFetching;

export const fetchTree = (
    treeType: TreeType,
    companyId: string
): ThunkAction<Promise<void>, {}, {}, AnyAction> =>
    async (dispatch, getState): Promise<void> => {
        const state = getState() as RootState;

        if (treeType === TreeType.geofencesTree) {
            if (isTreeFetching(state, treeType)) {
                cancelGeoTreeFetching();
            }

            if (getIsEditModeEnabled(state, TreeDisplayStateType.mainGeofencesTree)) {
                dispatch(disableEditMode(TreeDisplayStateType.mainGeofencesTree));
            }
            if (getSelectedNode(state, TreeDisplayStateType.mainGeofencesTree) !== null) {
                dispatch(replaceSelectedNode(TreeDisplayStateType.mainGeofencesTree, null));
            }
            if (getNodeInfoData(state, TreeDisplayStateType.mainGeofencesTree) !== null) {
                dispatch(replaceNodeInfoData(TreeDisplayStateType.mainGeofencesTree, null));
            }
            if (getNodeEditData(state, TreeDisplayStateType.mainGeofencesTree) !== null) {
                dispatch(replaceNodeEditData(TreeDisplayStateType.mainGeofencesTree, null));
            }
        }

        return new Promise<void>(resolve => {
            dispatch(replaceFetching(treeType, true));
            axios.get(`/api/v2/geozonestree?companyId=${companyId}`, {
                cancelToken: new axios.CancelToken(c => { cancelGeoTreeFetching = c; }),
            })
                .then((response: AxiosResponse) => {
                    dispatch(replaceItemsAndGroups(
                        response.data,
                        treeType
                    ));
                    dispatch(replaceFetching(treeType, false));
                    resolve();
                })
                .catch((error: AxiosError) => {
                    dispatch(replaceFetching(treeType, false));

                    if (error.response !== null && error.response.status !== 403)
                        showRequestError(error);

                    resolve();
                });
        });
    };

export const expandGroup = (
    treeDisplayState: TreeDisplayStateType,
    id: string
): ThunkAction<{}, RootState, {}, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const expanded = getExpandedGroupsIds(state, treeDisplayState);
        let newExpanded = [...expanded, id];

        return dispatch(replaceExpandedNodes(treeDisplayState, newExpanded, TreeNodeType.group));
    };

export const expandAll = (
    treeType: TreeType,
    treeDisplayState: TreeDisplayStateType,
): ThunkAction<{}, RootState, {}, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const allGroupsIds = getAllGroupsIds(state, treeType);

        return dispatch(replaceExpandedNodes(treeDisplayState, allGroupsIds, TreeNodeType.group));
    };

export const collapseGroup = (
    treeDisplayState: TreeDisplayStateType,
    id: string
): ThunkAction<{}, RootState, {}, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const expanded = getExpandedGroupsIds(state, treeDisplayState);
        let newExpanded = expanded.filter(item => item !== id);

        return dispatch(replaceExpandedNodes(treeDisplayState, newExpanded, TreeNodeType.group));
    };

export const collapseAll = (
    treeDisplayState: TreeDisplayStateType,
): ThunkAction<{}, {}, {}, AnyAction> =>
    dispatch => dispatch(replaceExpandedNodes(treeDisplayState, [], TreeNodeType.group));

export const checkNode = (
    treeType: TreeType,
    treeDisplayState: TreeDisplayStateType,
    nodeType: TreeNodeType,
    id: string
): ThunkAction<{}, RootState, {}, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        if (nodeType === TreeNodeType.item) {
            const checked = getCheckedNodesIds(state, treeDisplayState, nodeType);
            let newChecked = [...checked, id];

            dispatch(replaceCheckedNodes(treeDisplayState, newChecked, nodeType));
        }
        else {
            const allChildsIds = getAllChildsIds(
                state,
                treeType,
                id,
                {
                    [nodeType]: [id],
                    [TreeNodeType.item]: []
                },
            );
            const checkedGroups = getCheckedNodesIds(state, treeDisplayState, nodeType);
            let newCheckedGroups = [...checkedGroups, ...allChildsIds[nodeType]];
            const checkedItems = getCheckedNodesIds(state, treeDisplayState, TreeNodeType.item);
            let newCheckedItems = [...checkedItems, ...allChildsIds[TreeNodeType.item]];

            dispatch(replaceCheckedNodes(treeDisplayState, newCheckedGroups, nodeType));
            dispatch(replaceCheckedNodes(treeDisplayState, newCheckedItems, TreeNodeType.item));
        }

        dispatch(checkParentGroupIfNeeded(treeType, treeDisplayState, id, nodeType));

        return undefined;
    };

const getAllChildsIds = (
    state: RootState,
    treeType: TreeType,
    id: string,
    result: AllChildsIds,
): AllChildsIds => {
    let nodeData = getNodeData(state, treeType, id, TreeNodeType.group);
    let groupData = nodeData as FMTreeGroupData;
    result[TreeNodeType.group].push(...groupData.childGroups);
    result[TreeNodeType.item].push(...groupData.childItems);
    for (let groupId of groupData.childGroups) {
        result = getAllChildsIds(state, treeType, groupId, result);
    }

    return result;
};

interface AllChildsIds {
    [TreeNodeType.group]: string[];
    [TreeNodeType.item]: string[];
}

const checkParentGroupIfNeeded = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
    nodeId: string,
    nodeType: TreeNodeType,
) => (dispatch, getState) => {
    const state = getState();

    const parentId = getParentId(state, tree, nodeId, nodeType);
    if (parentId === '0') {
        return;
    }

    const childGroupsIds = getChildGroups(state, tree, parentId);
    const checkedGroupsIds = getCheckedNodesIds(state, displayState, TreeNodeType.group);
    for (let id of childGroupsIds) {
        if (!checkedGroupsIds.includes(id)) {
            return;
        }
    }

    const childItemsIds = getChildItems(state, tree, parentId);
    const checkedItemsIds = getCheckedNodesIds(state, displayState, TreeNodeType.item);
    for (let id of childItemsIds) {
        if (!checkedItemsIds.includes(id)) {
            return;
        }
    }

    let newCheckedGroupsIds = [...checkedGroupsIds, parentId];
    dispatch(replaceCheckedNodes(displayState, newCheckedGroupsIds, TreeNodeType.group));
    dispatch(checkParentGroupIfNeeded(tree, displayState, parentId, TreeNodeType.group));
};

export const checkAll = (
    treeType: TreeType,
    treeDisplayState: TreeDisplayStateType
): ThunkAction<{}, RootState, {}, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const allGroupsIds = getAllGroupsIds(state, treeType);
        const allItemsIds = getAllItemsIds(state, treeType);

        return dispatch(replaceCheckedNodes(treeDisplayState, allGroupsIds, TreeNodeType.group))
            && dispatch(replaceCheckedNodes(treeDisplayState, allItemsIds, TreeNodeType.item));
    };

export const uncheckNode = (
    treeType: TreeType,
    treeDisplayState: TreeDisplayStateType,
    nodeType: TreeNodeType,
    id: string
): ThunkAction<{}, RootState, {}, AnyAction> =>
    dispatch => {
        if (nodeType === TreeNodeType.item) {
            return dispatch(uncheckItem(treeType, treeDisplayState, id));
        }

        return dispatch(uncheckGroup(treeType, treeDisplayState, id));
    };

const uncheckItem = (
    treeType: TreeType,
    treeDisplayState: TreeDisplayStateType,
    id: string,
) => (dispatch, getState) => {
    const state = getState();

    const checked = getCheckedNodesIds(state, treeDisplayState, TreeNodeType.item);
    let newChecked = checked.filter(item => item !== id);

    const itemData = getNodeData(state, treeType, id, TreeNodeType.item) as FMTreeItemData;
    const parentChecked = isNodeChecked(state, treeDisplayState, itemData.dataForTree.parentId, TreeNodeType.group);
    if (parentChecked) {
        const parentsToUncheck = getAllParentsIds(state, treeType, id, TreeNodeType.item, []);
        dispatch(uncheckSomeGroups(treeDisplayState, parentsToUncheck));
    }

    return dispatch(replaceCheckedNodes(treeDisplayState, newChecked, TreeNodeType.item));
};

const uncheckSomeGroups = (
    displayState: TreeDisplayStateType,
    ids: string[],
) => (dispatch, getState) => {
    const state = getState();

    const checkedGroups = getCheckedNodesIds(state, displayState, TreeNodeType.group);
    let newCheckedGroups = checkedGroups.filter(id => !ids.includes(id));

    dispatch(replaceCheckedNodes(displayState, newCheckedGroups, TreeNodeType.group));
};

const uncheckGroup = (
    treeType: TreeType,
    treeDisplayState: TreeDisplayStateType,
    id: string,
) => (dispatch, getState) => {
    const state = getState();

    const nodesToUncheck = getAllChildsIds(
        state,
        treeType,
        id,
        {
            [TreeNodeType.group]: [id],
            [TreeNodeType.item]: []
        },
    );

    const groupData = getNodeData(state, treeType, id, TreeNodeType.group) as FMTreeGroupData;
    const parentChecked = isNodeChecked(state, treeDisplayState, groupData.parentId, TreeNodeType.group);
    if (parentChecked) {
        const parentsToUncheck = getAllParentsIds(state, treeType, id, TreeNodeType.group, []);
        nodesToUncheck[TreeNodeType.group].push(...parentsToUncheck);
    }

    const checkedGroups = getCheckedNodesIds(state, treeDisplayState, TreeNodeType.group);
    let newCheckedGroups = checkedGroups.filter(groupId => !nodesToUncheck[TreeNodeType.group].includes(groupId));
    const checkedItems = getCheckedNodesIds(state, treeDisplayState, TreeNodeType.item);
    let newCheckedItems = checkedItems.filter(itemId => !nodesToUncheck[TreeNodeType.item].includes(itemId));

    return dispatch(replaceCheckedNodes(treeDisplayState, newCheckedGroups, TreeNodeType.group))
        && dispatch(replaceCheckedNodes(treeDisplayState, newCheckedItems, TreeNodeType.item));
};

export const uncheckAll = (
    treeDisplayState: TreeDisplayStateType
): ThunkAction<{}, {}, {}, AnyAction> =>
    dispatch => dispatch(replaceCheckedNodes(treeDisplayState, [], TreeNodeType.group))
        && dispatch(replaceCheckedNodes(treeDisplayState, [], TreeNodeType.item));

export const searchNode = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
    text: string,
): ThunkAction<{}, RootState, {}, AnyAction> =>
    (dispatch, getState) => {
        if (text.length === 0) {
            return undefined;
        }
        text = text.toLowerCase();

        const state = getState();
        const searchData = getSearchData(state, displayState);

        const newSearchNeeded = text !== searchData.lastText;
        const foundNodes = newSearchNeeded
            ? getNodesForSearch(state, tree, text, '0', [])
            : searchData.foundNodes;

        if (foundNodes.length === 0) {
            return undefined;
        }

        let index = newSearchNeeded ? 0 : searchData.lastIndex + 1;
        if (index >= searchData.foundNodes.length) {
            index = 0;
        }

        const nodeToSelect = foundNodes[index];

        return dispatch(replaceSearchData(displayState, { lastText: text, foundNodes, lastIndex: index }))
            && dispatch(expandParentsAndSelectNode(tree, displayState, nodeToSelect.type, nodeToSelect.id));
    };

const getNodesForSearch = (
    state: RootState,
    treeType: TreeType,
    text: string,
    groupId: string,
    result: SelectedTreeNode[],
): SelectedTreeNode[] => {
    const groupData = getNodeData(state, treeType, groupId, TreeNodeType.group) as FMTreeGroupData;
    if (groupData.name.toLowerCase().includes(text)) {
        result.push({ id: groupId, type: TreeNodeType.group });
    }

    for (let id of groupData.childGroups) {
        result = getNodesForSearch(state, treeType, text, id, result);
    }

    for (let id of groupData.childItems) {
        let itemData = getNodeData(state, treeType, id, TreeNodeType.item) as FMTreeItemData;
        if (itemData.name.toLowerCase().includes(text)) {
            result.push({ id, type: TreeNodeType.item });
        }
    }

    return result;
};

export const expandParentsAndSelectNode = (
    treeType: TreeType,
    treeDisplayState: TreeDisplayStateType,
    type: TreeNodeType,
    id: string
): ThunkAction<{}, RootState, {}, AnyAction> =>
    async (dispatch, getState) => {
        const state = getState();
        const allParentsIds = getAllParentsIds(state, treeType, id, type, []);
        const collapsedParentsIds = getCollapsedGroupsByIds(state, treeDisplayState, allParentsIds);
        dispatch(expandSomeGroups(treeDisplayState, collapsedParentsIds));
        const data = await dispatch(selectNode(treeDisplayState, type, id));
        return data;
    };

const getAllParentsIds = (state: RootState, tree: TreeType, id: string, type: TreeNodeType, result: string[]) => {
    let parentId = getParentId(state, tree, id, type);
    if (parentId !== '0') {
        result.push(parentId);
    }
    return parentId === '0' ? result : getAllParentsIds(state, tree, parentId, TreeNodeType.group, result);
};

const getCollapsedGroupsByIds = (state: RootState, displayState: TreeDisplayStateType, ids: string[]) => {
    let expanded = getExpandedGroupsIds(state, displayState);

    return ids.filter(id => !expanded.includes(id));
};

export const expandSomeGroups = (
    displayState: TreeDisplayStateType,
    ids: string[],
): ThunkAction<{}, RootState, {}, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const expanded = getExpandedGroupsIds(state, displayState);
        let newExpanded = [...expanded, ...ids];

        return dispatch(replaceExpandedNodes(displayState, newExpanded, TreeNodeType.group));
    };

export const enableEditMode = (
    displayState: TreeDisplayStateType,
): ThunkAction<{}, RootState, {}, AnyAction> =>
    dispatch => dispatch(replaceInfoPanelEditMode(displayState, true));

export const disableEditMode = (
    displayState: TreeDisplayStateType,
): ThunkAction<{}, RootState, {}, AnyAction> =>
dispatch => dispatch(replaceInfoPanelEditMode(displayState, false));

export const updateEditNodeParam = (
    displayState: TreeDisplayStateType,
    sysName: string,
    value: any,
) => (dispatch, getState) => {
    let state = getState();
    const nodeData = getNodeEditData(state, displayState);

    let newData = { ...nodeData };
    newData.params = newData.params.map(param => param.sysName === sysName ? { ...param, value: value } : param);
    dispatch(replaceNodeEditData(displayState, newData));
};

export const updateGeofenceEditNodePoints = (
    displayState: TreeDisplayStateType,
    points: any[],
) => (dispatch, getState) => {
    let state = getState();
    const nodeData = getNodeEditData(state, displayState) as GeofenceEditData;
    let newData = { ...nodeData };
    newData.points = points;
    dispatch(replaceNodeEditData(displayState, newData));
};

export const saveGeotreeEditNode = (
    displayState: TreeDisplayStateType,
) => (dispatch, getState) => {
    const state = getState();
    const editNodeData = getNodeEditData(state, displayState);

    if (editNodeData.nodeType === TreeNodeType.group) {
        if (!checkGroupEditData(editNodeData)) {
            return;
        }

        if (editNodeData.id === '0') {
            dispatch(createGroup(TreeType.geofencesTree, displayState, editNodeData));
        }
        else {
            dispatch(updateGroup(TreeType.geofencesTree, displayState, state, editNodeData));
        }
        return;
    }

    if (editNodeData.nodeType === TreeNodeType.item) {
        const geofenceEditData = editNodeData as GeofenceEditData;
        if (!checkGeofenceEditData(geofenceEditData)) {
            return;
        }

        if (editNodeData.id === '0') {
            dispatch(createGeofence(displayState, geofenceEditData));
        }
        else {
            dispatch(updateGeofence(displayState, state, geofenceEditData));
        }
        return;
    }

    console.error('Unknown node type', editNodeData);
};

export const createGeofence = (
    displayState: TreeDisplayStateType,
    editNodeData: GeofenceEditData,
) => dispatch => {
    let params = getGeofenceParamsForRequest(editNodeData);
    dispatch(replaceInfoPanelFetching(displayState, true));
    axios.post('../../../api/v2/geozones', params)
        .then((response: AxiosResponse) => {          
            let newItemData = response.data;
            dispatch(addItemsToStore(
                TreeType.geofencesTree,
                displayState,
                [newItemData],
                newItemData.dataForTree.parentId,
            ));
        })
        .catch(showRequestError)
        .then(() => {
            dispatch(replaceInfoPanelFetching(displayState, false));
        });
};

export const createGroup = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
    editNodeData: NodeEditData,
) => dispatch => {
    let params = getParamsForRequest(editNodeData);
    dispatch(replaceInfoPanelFetching(displayState, true));
    axios.post('../../../api/v2/geozonegroups', params)
        .then((response: AxiosResponse) => {
            let newGroupData = response.data;
            dispatch(addGroupToStore(tree, displayState, newGroupData));
        })
        .catch(showRequestError)
        .then(() => dispatch(replaceInfoPanelFetching(displayState, false)));;
};

export const updateGeofence = (
    displayState: TreeDisplayStateType,
    state: RootState,
    editNodeData: GeofenceEditData,
) => dispatch => {
    let params = getGeofenceParamsForRequest(editNodeData);
    dispatch(replaceInfoPanelFetching(displayState, true));
    axios.put(`../../../api/v2/geozones/${editNodeData.id}`, params)
        .then((response: AxiosResponse) => {
            let tree = TreeType.geofencesTree;
            let newGeofenceData: FMTreeGeofenceItemData = response.data;
            dispatch(deleteNodeFromStore(tree, displayState, state, editNodeData.id, TreeNodeType.item));
            dispatch(addItemsToStore(tree, displayState, [newGeofenceData], newGeofenceData.dataForTree.parentId));
        })
        .catch(showRequestError)
        .then(() => dispatch(replaceInfoPanelFetching(displayState, false)));
};

export const updateGroup = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
    state: RootState,
    editNodeData: NodeEditData,
) => dispatch => {
    let params = getParamsForRequest(editNodeData);
    dispatch(replaceInfoPanelFetching(displayState, true));
    axios.put(`../../../api/v2/geozonegroups/${editNodeData.id}`, params)
        .then((response: AxiosResponse) => {
            const groupData = getNodeData(state, tree, editNodeData.id, TreeNodeType.group) as FMTreeGroupData;
            let newGroupData: FMTreeGroupData = response.data;
            newGroupData.childGroups = groupData.childGroups;
            newGroupData.childItems = groupData.childItems;
            dispatch(deleteNodeFromStore(tree, displayState, state, editNodeData.id, TreeNodeType.group));
            dispatch(addGroupToStore(tree, displayState, newGroupData));
        })
        .catch(showRequestError)
        .then(() =>  dispatch(replaceInfoPanelFetching(displayState, false)));
};

const getParamsForRequest = (editNodeData: NodeEditData) => {
    let result = [];
    for (let param of editNodeData.params) {
        if (editNodeData.nodeType === TreeNodeType.item && param.sysName === ParamSysName.type) {
            param.value = (editNodeData as GeofenceEditData).geofenceType;
        }
        result.push({ sysName: param.sysName, value: param.value });
    }
    return result;
};

const getGeofenceParamsForRequest = (editNodeData: GeofenceEditData) => {
    let result = [];
    for (let param of editNodeData.params) {
        if (param.sysName === ParamSysName.type) {
            param.value = editNodeData.geofenceType;
        }
        result.push({ sysName: param.sysName, value: param.value });
    }
    result.push({ sysName: 'points', value: JSON.stringify(editNodeData.points) });

    return result;
};

const checkGroupEditData = (editNodeData: NodeEditData): boolean => {
    for (let param of editNodeData.params) {
        if (!checkParamEditData(param, editNodeData.nodeType === TreeNodeType.item)) {
            return false;
        }
    }

    return true;
};

const checkParamEditData = (nodeParam: NodeParam, isItem: boolean): boolean => {
    if (!nodeParam.editable) {
        return true;
    }

    switch (nodeParam.type) {
        case ParamType.text: return checkText(nodeParam as NodeTextParam);
        case ParamType.parentGroup: return checkParentGroup(nodeParam, isItem);
        case ParamType.number: return checkNumber(nodeParam as NodeNumberParam);
        case ParamType.color: return checkColor(nodeParam);
        case ParamType.icon:
        case ParamType.check: return true;
        default:
            console.error('Unknown parameter type', nodeParam);
            alert(fmtools.translate('Error'));
            return false;
    }
};

const checkText = (nodeParam: NodeTextParam): boolean => {
    if (nodeParam.sysName === ParamSysName.name && nodeParam.value.length < 1) {
        alert(fmtools.translate('ErrorNazvanieEmpty'));
        return false;
    }

    if (nodeParam.value.length > nodeParam.maxLength) {
        nodeParam.label = nodeParam.label.substring(0, nodeParam.maxLength);
        console.warn(`${nodeParam.sysName} was sliced due to large length.`);
    }

    return true;
};

const checkParentGroup = (nodeParam: NodeParam, needParent: boolean) => {
    if (needParent && (!nodeParam.value || nodeParam.value === '0')) {
        alert(fmtools.translate('ParentGroupNotSelected'));
        return false;
    }

    return true;
};

const checkNumber = (nodeParam: NodeNumberParam) => {
    if (nodeParam.decimalPrecision === 0) {
        nodeParam.value = parseInt(nodeParam.value, 10);
    }

    if (nodeParam.value < nodeParam.min) {
        nodeParam.value = nodeParam.min;
        console.warn(`${nodeParam.sysName} value was uncreased to min value.`);
    }

    if (nodeParam.value > nodeParam.max) {
        nodeParam.value = nodeParam.max;
        console.warn(`${nodeParam.sysName} value was decreased to max value.`);
    }

    return true;
};

const checkColor = (nodeParam: NodeParam) => {
    let formatOk = /^#[0-9A-F]{6}$/i.test(nodeParam.value);

    if (!formatOk) {
        nodeParam.value = '#3366CC';
        console.warn(`${nodeParam.sysName} value was changed to default due to wrong format.`);
    }

    return true;
};

const checkGeofenceEditData = (editNodeData: GeofenceEditData): boolean => {
    for (let param of editNodeData.params) {
        if (!checkParamEditData(param, editNodeData.nodeType === TreeNodeType.item)) {
            return false;
        }
    }

    let needMorePoints: boolean;
    switch (editNodeData.geofenceType) {
        case GeofenceType.line: needMorePoints = editNodeData.points.length < 2; break;
        case GeofenceType.polygon: needMorePoints = editNodeData.points.length < 3; break;
        default: needMorePoints = editNodeData.points.length < 1; break;
    }
    if (needMorePoints) {
        alert(fmtools.translate('NoPoints'));
        return false;
    }

    return true;
};

export const fetchGeotreeNodeData = (forEdit = false): ThunkAction<Promise<void>, RootState, {}, AnyAction> =>
    async (dispatch, getState): Promise<void> => {
        const state = getState();
        const selectedNode = getSelectedNode(state, TreeDisplayStateType.mainGeofencesTree);
        let requestTarget = 'geozones';
        if (selectedNode.type === TreeNodeType.group) {
            requestTarget = 'geozonegroups';
        }
        let requestType = forEdit ? 'edit' : 'info';
        return new Promise<void>(
            resolve => {
                dispatch(replaceInfoPanelFetching(TreeDisplayStateType.mainGeofencesTree, true));
                axios.get(`../../../api/v2/${requestTarget}/${selectedNode.id}/${requestType}`)
                    .then((response: AxiosResponse) => {
                        let action = forEdit ? replaceNodeEditData : replaceNodeInfoData;
                        dispatch(action(
                            TreeDisplayStateType.mainGeofencesTree,
                            response.data,
                        ));
                        if (forEdit) {
                            dispatch(enableEditMode(TreeDisplayStateType.mainGeofencesTree));
                        }
                        resolve(response.data);
                    })
                    .catch((error: AxiosError) => {
                        showRequestError(error);
                        resolve();
                    })
                    .then(() =>  dispatch(replaceInfoPanelFetching(TreeDisplayStateType.mainGeofencesTree, false)));
            },
        );
    };

export const fetchGeotreeNewNodeData = (
    nodeType = TreeNodeType.group,
    geofenceType = GeofenceType.point,
): ThunkAction<Promise<void>, RootState, {}, AnyAction> =>
    async (dispatch, getState): Promise<void> => {
        let requestTarget = nodeType === TreeNodeType.group
            ? 'geozonegroups'
            : 'geozones';
        let requestType = 'create';
        let queryParams = nodeType === TreeNodeType.group
            ? ''
            : `?geoType=${geofenceType}`;
        return new Promise<void>(
            resolve => {
                const state = getState();
                const allGroupsIds = getAllGroupsIds(state, TreeType.geofencesTree);
                if (allGroupsIds.length === 0 && nodeType === TreeNodeType.item) {
                    alert(fmtools.translate('CreateGroupBeforeGeozone'));
                    resolve();
                    return;
                }
                dispatch(replaceInfoPanelFetching(TreeDisplayStateType.mainGeofencesTree, true));
                axios.get(`../../../api/v2/${requestTarget}/${requestType}${queryParams}`)
                    .then((response: AxiosResponse) => {
                        // set parent group
                        const selectedNode = getSelectedNode(state, TreeDisplayStateType.mainGeofencesTree);
                        if (selectedNode) {
                            let parentGroup = '0';
                            if (selectedNode.type === TreeNodeType.item) {
                                parentGroup = getParentId(state, TreeType.geofencesTree, selectedNode.id, selectedNode.type);
                            }
                            else {
                                parentGroup = selectedNode.id;
                            }
                            for (let param of response.data.params) {
                                if (param.sysName === 'parent_group') {
                                    param.value = parentGroup;
                                    break;
                                }
                            }
                        }
                        // write data to store
                        dispatch(replaceNodeEditData(
                            TreeDisplayStateType.mainGeofencesTree,
                            response.data,
                        ));
                        dispatch(enableEditMode(TreeDisplayStateType.mainGeofencesTree));
                        resolve(response.data);
                    })
                    .catch((error: AxiosError) => {
                        showRequestError(error);
                        resolve();
                    })
                    .then(() =>  dispatch(replaceInfoPanelFetching(TreeDisplayStateType.mainGeofencesTree, false)));
            },
        );
    };

export const showAsTree = (
    displayState: TreeDisplayStateType,
): ThunkAction<{}, RootState, {}, AnyAction> =>
    dispatch => dispatch(replaceTreeViewType(displayState, TreeViewType.tree));

export const showAsList = (
    displayState: TreeDisplayStateType,
): ThunkAction<{}, RootState, {}, AnyAction> =>
    dispatch => dispatch(replaceTreeViewType(displayState, TreeViewType.list));

export const deleteSelectedNode = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
): ThunkAction<Promise<void>, RootState, {}, AnyAction> =>
    async (dispatch, getState) => {
        const state = getState();
        const selectedNode = getSelectedNode(state, displayState);
        if (selectedNode === null) {
            return;
        }

        const nodeData = getNodeData(state, tree, selectedNode.id, selectedNode.type);
        let requestTarget = 'geozones';

        if (selectedNode.type === TreeNodeType.group) {
            const groupData = nodeData as FMTreeGroupData;
            if (groupData.childGroups.length > 0 || groupData.childItems.length > 0) {
                alert(fmtools.translate('ErrorGroupGeoGeozones'));
                return;
            }

            requestTarget = 'geozonegroups';
        }

        let confirmText = selectedNode.type === TreeNodeType.group
            ? fmtools.translate('confirm_del_zone_grp')
            : fmtools.translate('confirm_del_zone');
        let deleteConfirmed = confirm(confirmText);
        if (!deleteConfirmed) {
            return;
        }

        return new Promise<void>(
            resolve => {
                dispatch(replaceFetching(tree, true));
                axios.delete(`../../../api/v2/${requestTarget}/${selectedNode.id}`)
                    .then((response: AxiosResponse) => {
                        if (response.data.result === false) {
                            alert(response.data.errorText);
                        }
                        else {
                            dispatch(deleteNodeFromStore(tree, displayState, state, nodeData.id, selectedNode.type));
                        }
                        resolve();
                    })
                    .catch((error: AxiosError) => {
                        showRequestError(error);
                        resolve();
                    })
                    .then(()=> dispatch(replaceFetching(tree, false)));
            },
        );
    };

const deleteNodeFromStore = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
    state: RootState,
    nodeId: string,
    nodeType: TreeNodeType,
) => dispatch => {
    const isGroup = nodeType === TreeNodeType.group;
    const parentId = getParentId(state, tree, nodeId, nodeType);

    // delete node childs of parent
    const children = isGroup
        ? getChildGroups(state, tree, parentId)
        : getChildItems(state, tree, parentId);
    let newChildren = children.filter(id => id !== nodeId);
    if (isGroup) {
        dispatch(replaceChildGroupsIds(tree, parentId, newChildren));
    }
    else {
        dispatch(replaceChildItemsIds(tree, parentId, newChildren));
    }

    // delete node from allIds
    const allIds = isGroup
        ? getAllGroupsIds(state, tree)
        : getAllItemsIds(state, tree);
    let newAllIds = allIds.filter(id => id !== nodeId);
    dispatch(replaceAllIds(tree, nodeType, newAllIds));

    // clear selected node if needed
    const selectedNode = getSelectedNode(state, displayState);
    if (selectedNode !== null && nodeId === selectedNode.id && nodeType === selectedNode.type) {
        dispatch(replaceSelectedNode(displayState, null));
        dispatch(replaceNodeInfoData(displayState, null));
    }

    // remove edit mode if needed
    const editModeEnabled = getIsEditModeEnabled(state, displayState);
    if (editModeEnabled) {
        dispatch(replaceInfoPanelEditMode(displayState, false));
    }

    return undefined;
};

export const deleteCheckedOrSelectedItems = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
): ThunkAction<Promise<void>, RootState, {}, AnyAction> =>
    async (dispatch, getState) => {
        const state = getState();
        const checkedItems = getCheckedNodesIds(state, displayState, TreeNodeType.item);
        const selectedNode = getSelectedNode(state, displayState);

        let itemsToDelete = [];
        if (checkedItems.length > 0) {
            itemsToDelete.push(...checkedItems);
        }
        else if (selectedNode !== null && selectedNode.type === TreeNodeType.item) {
            itemsToDelete.push(selectedNode.id);
        }

        if (itemsToDelete.length === 0) {
            return;
        }

        let deleteConfirmed = confirm(fmtools.translate('confirm_del_zone'));
        if (!deleteConfirmed) {
            return;
        }

        return new Promise<void>(
            resolve => {
                dispatch(replaceFetching(tree, true));
                axios.delete('../../../api/v2/geozones', {
                    data: itemsToDelete,
                })
                    .then((response: AxiosResponse) => {
                        let deletedItems = [];
                        let errorText = '';
                        for (let item of response.data) {
                            if (item.result) {
                                deletedItems.push(item.id.toString());
                            }
                            else {
                                console.warn(item.errorText);
                                errorText += item.errorText + '\r\n';
                            }
                        }

                        if (errorText.length > 0) {
                            alert(errorText);
                        }

                        for (let id of deletedItems) {
                            const currentState = getState();
                            dispatch(deleteNodeFromStore(tree, displayState, currentState, id, TreeNodeType.item));
                        }
                        resolve();
                    })
                    .catch((error: AxiosError) => {
                        showRequestError(error);
                        resolve();
                    })
                    .then(() => dispatch(replaceFetching(tree, false)));
            },
        );
    };

export const exportCheckedOrSelectedItems = (
    displayState: TreeDisplayStateType,
    fileType: string,
): ThunkAction<Promise<void>, RootState, {}, AnyAction> =>
    (dispatch, getState) => {
        if (displayState !== TreeDisplayStateType.mainGeofencesTree) {
            return undefined;
        }

        const state = getState();
        const checkedItems = getCheckedNodesIds(state, displayState, TreeNodeType.item);
        const selectedNode = getSelectedNode(state, displayState);

        let itemsToExport = [];
        if (checkedItems.length > 0) {
            itemsToExport.push(...checkedItems);
        }
        else if (selectedNode !== null && selectedNode.type === TreeNodeType.item) {
            itemsToExport.push(selectedNode.id);
        }

        if (itemsToExport.length === 0) {
            return undefined;
        }

        return new Promise<void>(
            resolve => {
                try {
                    Ext.toast(fmtools.translate('RequestSended'));
                }
                catch (err) {
                    console.warn('error to show Ext toast: ', fmtools.translate('RequestSended'));
                }
                let url = '../../../api/v2/geozones/export';
                if (fileType == "excel")
                    url = '../../../api/v2/geozones/exporttoexcel';

                axios.post(url, itemsToExport)
                    .then((response: AxiosResponse) => {
                        if (response.data.result !== 'Error') {
                            // window.open(`../../../${response.data}`, 'blank');
                            let url = `../../../${response.data}`;
                            let downloadLink = document.createElement('a');
                            downloadLink.setAttribute('href', url);
                            downloadLink.setAttribute('download', url.slice(url.lastIndexOf('/') + 1));
                            downloadLink.click();
                            downloadLink = undefined;
                        }
                        resolve();
                    })
                    .catch((error: AxiosError) => {
                        showRequestError(error);
                        resolve();
                    });
            },
        );
    };

export const addGroupToStore = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
    nodeData: FMTreeGroupData,
) => (dispatch, getState) => {
    const state = getState();

    // add group to byIds and allIds
    let newByIds = { ...getGroupsByIds(state, tree) };
    newByIds[nodeData.id] = nodeData;
    let newAllIds = [...getAllGroupsIds(state, tree)];
    let indexToInsert = getIndexToInsertNode(state, tree, nodeData, newAllIds, 0, TreeNodeType.group);
    newAllIds.splice(indexToInsert, 0, nodeData.id);
    dispatch(replaceGroupsData(tree, newByIds, newAllIds));

    // add group to childs
    let newChildGroupsIds = [...getChildGroups(state, tree, nodeData.parentId)];
    indexToInsert = getIndexToInsertNode(state, tree, nodeData, newChildGroupsIds, 0, TreeNodeType.group);
    newChildGroupsIds.splice(indexToInsert, 0, nodeData.id);
    dispatch(replaceChildGroupsIds(tree, nodeData.parentId, newChildGroupsIds));

    // select new group
    dispatch(expandParentsAndSelectNode(tree, displayState, TreeNodeType.group, nodeData.id));
};

export const addItemsToStore = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
    someItems: FMTreeItemDataType[],
    parentId: string,
) => (dispatch, getState) => {
    if (someItems.length === 0) {
        return;
    }

    const state = getState();

    // add items to byIds and allIds
    let newByIds = { ...getItemsByIds(state, tree) };
    let newAllIds = [...getAllItemsIds(state, tree)];
    let indexToInsert = 0;
    for (let item of someItems) {
        newByIds[item.id] = item;
        indexToInsert = getIndexToInsertNode(state, tree, item, newAllIds, indexToInsert);
        newAllIds.splice(indexToInsert, 0, item.id);
        indexToInsert += 1;
    }
    dispatch(replaceItemsData(tree, newByIds, newAllIds));

    // add items to childs
    const newState = getState();
    let newChildItemsIds = [...getChildItems(newState, tree, parentId)];
    indexToInsert = 0;
    for (let item of someItems) {
        indexToInsert = getIndexToInsertNode(newState, tree, item, newChildItemsIds, indexToInsert);
        newChildItemsIds.splice(indexToInsert, 0, item.id);
        indexToInsert += 1;
    }
    dispatch(replaceChildItemsIds(tree, parentId, newChildItemsIds));

    // select last item from array
    let lastItemId = someItems[someItems.length - 1].id;
    dispatch(expandParentsAndSelectNode(tree, displayState, TreeNodeType.item, lastItemId));
};

const getIndexToInsertNode = (
    state: RootState,
    tree: TreeType,
    item: FMTreeItemDataType | FMTreeGroupData,
    targetArray: string[],
    startIndex: number,
    nodeType = TreeNodeType.item,
): number => {
    let indexToInsert = startIndex;
    if (targetArray.length === 0) {
        return 0;
    }
    if (indexToInsert >= targetArray.length) {
        return targetArray.length;
    }
    let currentNodeName = getNodeData(state, tree, targetArray[indexToInsert], nodeType).name.toLowerCase();
    while (indexToInsert < targetArray.length && currentNodeName < item.name.toLocaleLowerCase()
    ) {
        indexToInsert += 1;
        if (indexToInsert < targetArray.length) {
            currentNodeName = getNodeData(state, tree, targetArray[indexToInsert], nodeType).name.toLowerCase();
        }
    }

    return indexToInsert;
};

const showRequestError = (error: AxiosError) => {
    console.error(error, error.response ? error.response : '');
    if (error.response && error.response.status < 500) {
        alert(error.response.data.Message);
    }
};

export const importItems = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
    content: string,
    parentId: string,
) => dispatch => {
    switch (tree) {
        case TreeType.geofencesTree:
            return dispatch(importGeofences(tree, displayState, content, parentId));
    }
};

export const importGeofences = (
    tree: TreeType,
    displayState: TreeDisplayStateType,
    content: string,
    parentId: string,
) => async dispatch =>
    new Promise<void>(
        resolve => {
            axios.post(`../../../api/v2/geozones/import?parentGroupId=${parentId}`, { content })
            .then((response: AxiosResponse) => {
                for (let errorText of response.data.failed) {
                    console.warn(errorText);
                }
                if (response.data.withoutNames > 0) {
                    console.warn(`File contains ${response.data.withoutNames} geofences without name`);
                    alert(fmtools.translate('ImportGeofencesHasNoName'));
                }
                dispatch(addItemsToStore(
                    tree,
                    displayState,
                    response.data.success,
                    parentId,
                ));
                resolve();
            })
            .catch((error: AxiosError) => {
                resolve();
                console.error(error);
                alert(fmtools.translate('api_request_error'));
            });
        },
    );

export const replaceInfoPanelOptimizeMode = (
  treeDisplayState: string,
  infoPanelInOptimizeMode: boolean,
): ReplaceInfoPanelOptimizeMode => ({
    type: constants.REPLACE_INFO_PANEL_OPTIMIZE_MODE,
    treeDisplayState,
    infoPanelInOptimizeMode,
});

interface OptimizationResult {
    points: GeoPoint[];
    oldInfo: OptimizationPointInfo;
    newInfo: OptimizationPointInfo;
    distBetweenPoints: number;
    squareDeviationPercentage: number;
    pointsCompressRate: number;
}

interface OptimizationPointInfo {
    pointsCount: number;
    square: number;
    pointsPerHectare: number;
}

export const optimizeGeofence = () => async (dispatch, getState) => {
    try {
        dispatch(startLoadingOptimize());
        const state = getState();
        const displayState = TreeDisplayStateType.mainGeofencesTree;
        const treeState = state.fmTrees.fmTreesDisplay[displayState];
        const optimizeData = treeState.optimizeData;
        const id = treeState.selectedNode.id;
        let minDistance = optimizeData.minDistanceBetweenPoints;
        if (isNaN(minDistance)) {
            minDistance = MIN_DISTANCE_BETWEEN_POINTS_DEFAULT_VALUE;
        }
        if (minDistance < MIN_DISTANCE_BETWEEN_POINTS_MIN_VALUE) {
            minDistance = MIN_DISTANCE_BETWEEN_POINTS_MIN_VALUE;
        }
        let allowedDeviation = optimizeData.allowedDeviationFromLine;
        if (isNaN(allowedDeviation)) {
            allowedDeviation = ALLOWED_DEVIATION_FROM_LINE_DEFAULT_VALUE;
        }
        if (allowedDeviation < ALLOWED_DEVIATION_FROM_LINE_MIN_VALUE) {
            allowedDeviation = ALLOWED_DEVIATION_FROM_LINE_MIN_VALUE;
        }
        let url = `../../../api/v2/geozones/${id}/minimizepoints`;
        url += `?minDistanceBetweenPoints=${minDistance}`;
        url += `&allowedDeviationFromLine=${allowedDeviation}`;
        const response = await axios.get(url);
        const requestResult = response.data as OptimizationResult;
        const newOptimizeData: OptimizeData = {
            minDistanceBetweenPoints: requestResult.distBetweenPoints,
            allowedDeviationFromLine: optimizeData.allowedDeviationFromLine,
            optimizationComplete: true,
            currentPointsCount: requestResult.oldInfo.pointsCount,
            newPointsCount: requestResult.newInfo.pointsCount,
            currentArea: requestResult.oldInfo.square,
            newArea: requestResult.newInfo.square,
            areaDeviationValue: requestResult.squareDeviationPercentage,
            pointsCompressRate: requestResult.pointsCompressRate,
            points: requestResult.points,
            isFetching: false,
        };
        dispatch(closeOptimizeEditMode());
        dispatch(replaceOptimizeData(newOptimizeData));
    }
    catch (e) {
        showRequestError(e);
    }
};

export const replaceOptimizeData = (data: OptimizeData): ReplaceOptimizeData => ({
    type: constants.REPLACE_OPTIMIZE_DATA,
    payload: data,
});

export const replaceOptimizeMinDistanceBetweenPoints = (payload: number): ReplaceOptimizeMinDistanceBetweenPoints => ({
    type: constants.REPLACE_OPTIMIZE_MIN_DISTANCE_BETWEEN_POINTS,
    payload,
});

export const replaceOptimizeAllowedDeviationFromLine = (payload: number): ReplaceOptimizeAllowedDeviationFromLine => ({
    type: constants.REPLACE_OPTIMIZE_ALLOWED_DEVIATION_FROM_LINE,
    payload,
});

export const closeOptimizeEditMode = (): CloseOptimizeEditMode => ({
    type: constants.CLOSE_OPTIMIZE_EDIT_MODE,
});

export const replaceHasChanged = (payload: boolean): ReplaceEditHasChanged => ({
    type: constants.REPLACE_EDIT_HAS_CHANGED,
    payload,
});

export const updateArea = (points: GeoPoint[]) => async (dispatch) => {
    try {
        let url = `../../../api/v2/geozones/square`;
        const response = await axios.post(url, points);
        const requestResult = response.data as number;
        dispatch({
            type: constants.REPLACE_NEW_AREA,
            payload: requestResult,
        });
    }
    catch (e) {
        showRequestError(e);
    }
};

export const saveOptimizedGeofence = (
    points: GeoPoint[],
) => async (dispatch, getState) => {
    const state = getState();
    const displayState = TreeDisplayStateType.mainGeofencesTree;
    const id = state.fmTrees.fmTreesDisplay[displayState].selectedNode.id;
    dispatch(replaceInfoPanelFetching(displayState, true));
    try {
        const response = await axios.post(`../../../api/v2/geozones/${id}/updatepoints`, points);
        const treeType = TreeType.geofencesTree;
        const newGeofence = response.data as FMTreeGeofenceItemData;
        dispatch(deleteNodeFromStore(treeType, displayState, state, id, TreeNodeType.item));
        dispatch(addItemsToStore(treeType, displayState, [newGeofence], newGeofence.dataForTree.parentId));
    }
    catch (e) {
        showRequestError(e);
    }
    dispatch(replaceInfoPanelFetching(displayState, false));
};

export const startLoadingOptimize = (): ReplaceOptimizeFetching => ({
    type: constants.REPLACE_OPTIMIZE_FETCHING,
    payload: true,
});

export const isAllowEditGeoTree = () => {
    // @ts-ignore
    return window.permission.IsEditGeozone;
};
